import { sumScore, type ScoreAnswers, type ScoreItem } from './scores';
import type { Severity } from './vitals';

// ---------------------------------------------------------------------------------------------
// BE-FAST
// ---------------------------------------------------------------------------------------------

export const BEFAST_ITEMS = [
  { id: 'balance', letter: 'B', label: 'Balance', prompt: 'Sudden loss of balance or coordination?' },
  { id: 'eyes', letter: 'E', label: 'Eyes', prompt: 'Sudden loss of vision or double vision?' },
  { id: 'face', letter: 'F', label: 'Face', prompt: 'Facial droop when smiling?' },
  { id: 'arms', letter: 'A', label: 'Arms', prompt: 'Arm weakness or drift when both arms are raised?' },
  { id: 'speech', letter: 'S', label: 'Speech', prompt: 'Slurred speech or unable to repeat a simple phrase?' },
] as const;

export type BefastFindings = Partial<Record<(typeof BEFAST_ITEMS)[number]['id'], boolean>>;

export function befastPositive(findings: BefastFindings): boolean {
  return Object.values(findings).some(Boolean);
}

// ---------------------------------------------------------------------------------------------
// Cincinnati Prehospital Stroke Scale
// ---------------------------------------------------------------------------------------------

export const CINCINNATI_ITEMS = [
  { id: 'face', label: 'Facial droop', normal: 'Both sides of face move equally', abnormal: 'One side does not move as well as the other' },
  { id: 'arm', label: 'Arm drift', normal: 'Both arms move equally or not at all', abnormal: 'One arm drifts down compared with the other' },
  { id: 'speech', label: 'Speech', normal: 'Uses correct words with no slurring', abnormal: 'Slurred or inappropriate words, or mute' },
] as const;

export type CincinnatiFindings = Partial<Record<(typeof CINCINNATI_ITEMS)[number]['id'], boolean>>;

export function interpretCincinnati(findings: CincinnatiFindings) {
  const abnormal = Object.values(findings).filter(Boolean).length;
  return {
    abnormal,
    positive: abnormal > 0,
    detail:
      abnormal === 0
        ? 'No abnormal findings. A negative screen does not exclude stroke (e.g. posterior circulation).'
        : `${abnormal} abnormal finding${abnormal > 1 ? 's' : ''}: one abnormal finding carries ~72% probability of stroke; all three > 85%.`,
  };
}

// ---------------------------------------------------------------------------------------------
// Los Angeles Motor Scale (LAMS)
// ---------------------------------------------------------------------------------------------

export const LAMS_ITEMS: ScoreItem[] = [
  {
    id: 'face',
    label: 'Facial droop',
    options: [
      { score: 0, label: 'Absent' },
      { score: 1, label: 'Present' },
    ],
  },
  {
    id: 'arm',
    label: 'Arm drift',
    options: [
      { score: 0, label: 'Absent' },
      { score: 1, label: 'Drifts down' },
      { score: 2, label: 'Falls rapidly' },
    ],
  },
  {
    id: 'grip',
    label: 'Grip strength',
    options: [
      { score: 0, label: 'Normal' },
      { score: 1, label: 'Weak grip' },
      { score: 2, label: 'No grip' },
    ],
  },
];

export function interpretLams(total: number): { severity: Severity; label: string } {
  return total >= 4
    ? { severity: 'critical', label: 'LAMS ≥ 4: large vessel occlusion likely — consider comprehensive/thrombectomy-capable stroke center' }
    : { severity: 'normal', label: 'LAMS < 4: LVO less likely' };
}

// ---------------------------------------------------------------------------------------------
// RACE (Rapid Arterial oCclusion Evaluation)
// ---------------------------------------------------------------------------------------------

export type HemiparesisSide = 'left' | 'right';

export const RACE_ITEMS: ScoreItem[] = [
  {
    id: 'face',
    label: 'Facial palsy',
    options: [
      { score: 0, label: 'Absent (symmetric movement)' },
      { score: 1, label: 'Mild (slightly asymmetric)' },
      { score: 2, label: 'Moderate to severe (completely asymmetric)' },
    ],
  },
  {
    id: 'arm',
    label: 'Arm motor function',
    options: [
      { score: 0, label: 'Normal to mild (limb upheld > 10 s)' },
      { score: 1, label: 'Moderate (limb upheld < 10 s)' },
      { score: 2, label: 'Severe (unable to raise against gravity)' },
    ],
  },
  {
    id: 'leg',
    label: 'Leg motor function',
    options: [
      { score: 0, label: 'Normal to mild (limb upheld > 5 s)' },
      { score: 1, label: 'Moderate (limb upheld < 5 s)' },
      { score: 2, label: 'Severe (unable to raise against gravity)' },
    ],
  },
  {
    id: 'gaze',
    label: 'Head and gaze deviation',
    options: [
      { score: 0, label: 'Absent' },
      { score: 1, label: 'Present' },
    ],
  },
];

export const RACE_APHASIA: ScoreItem = {
  id: 'cortical',
  label: 'Aphasia (right hemiparesis)',
  hint: 'Ask patient to "close your eyes" and "make a fist".',
  options: [
    { score: 0, label: 'Performs both tasks correctly' },
    { score: 1, label: 'Performs one task correctly' },
    { score: 2, label: 'Performs neither task' },
  ],
};

export const RACE_AGNOSIA: ScoreItem = {
  id: 'cortical',
  label: 'Agnosia (left hemiparesis)',
  hint: 'Ask "Whose arm is this?" (showing affected arm) and "Can you move your arm?"',
  options: [
    { score: 0, label: 'Recognizes arm and impairment' },
    { score: 1, label: 'Does not recognize arm or impairment' },
    { score: 2, label: 'Recognizes neither' },
  ],
};

export function raceItems(side: HemiparesisSide): ScoreItem[] {
  return [...RACE_ITEMS, side === 'right' ? RACE_APHASIA : RACE_AGNOSIA];
}

export function interpretRace(total: number): { severity: Severity; label: string } {
  return total >= 5
    ? { severity: 'critical', label: 'RACE ≥ 5: large vessel occlusion likely' }
    : { severity: 'normal', label: 'RACE < 5: LVO less likely' };
}

// ---------------------------------------------------------------------------------------------
// NIH Stroke Scale
// ---------------------------------------------------------------------------------------------

const motorArm = (side: 'Left' | 'Right', id: string): ScoreItem => ({
  id,
  label: `5${id.endsWith('L') ? 'a' : 'b'}. Motor arm — ${side}`,
  hint: 'Extend arm 90° (sitting) or 45° (supine) for 10 seconds.',
  allowUntestable: true,
  options: [
    { score: 0, label: 'No drift for full 10 seconds' },
    { score: 1, label: 'Drift; does not hit bed or other support' },
    { score: 2, label: 'Some effort against gravity; drifts to bed' },
    { score: 3, label: 'No effort against gravity; limb falls' },
    { score: 4, label: 'No movement' },
  ],
});

const motorLeg = (side: 'Left' | 'Right', id: string): ScoreItem => ({
  id,
  label: `6${id.endsWith('L') ? 'a' : 'b'}. Motor leg — ${side}`,
  hint: 'Hold leg at 30° (supine) for 5 seconds.',
  allowUntestable: true,
  options: [
    { score: 0, label: 'No drift for full 5 seconds' },
    { score: 1, label: 'Drift; does not hit bed' },
    { score: 2, label: 'Some effort against gravity; falls to bed by 5 s' },
    { score: 3, label: 'No effort against gravity; falls immediately' },
    { score: 4, label: 'No movement' },
  ],
});

export const NIHSS_ITEMS: ScoreItem[] = [
  {
    id: '1a',
    label: '1a. Level of consciousness',
    options: [
      { score: 0, label: 'Alert; keenly responsive' },
      { score: 1, label: 'Not alert; arousable by minor stimulation' },
      { score: 2, label: 'Not alert; requires repeated or painful stimulation' },
      { score: 3, label: 'Reflex responses only or totally unresponsive' },
    ],
  },
  {
    id: '1b',
    label: '1b. LOC questions',
    hint: 'Ask the month and the patient’s age.',
    options: [
      { score: 0, label: 'Answers both correctly' },
      { score: 1, label: 'Answers one correctly' },
      { score: 2, label: 'Answers neither correctly' },
    ],
  },
  {
    id: '1c',
    label: '1c. LOC commands',
    hint: 'Open and close eyes; grip and release non-paretic hand.',
    options: [
      { score: 0, label: 'Performs both correctly' },
      { score: 1, label: 'Performs one correctly' },
      { score: 2, label: 'Performs neither correctly' },
    ],
  },
  {
    id: '2',
    label: '2. Best gaze',
    options: [
      { score: 0, label: 'Normal' },
      { score: 1, label: 'Partial gaze palsy' },
      { score: 2, label: 'Forced deviation or total gaze paresis' },
    ],
  },
  {
    id: '3',
    label: '3. Visual fields',
    options: [
      { score: 0, label: 'No visual loss' },
      { score: 1, label: 'Partial hemianopia' },
      { score: 2, label: 'Complete hemianopia' },
      { score: 3, label: 'Bilateral hemianopia (blind)' },
    ],
  },
  {
    id: '4',
    label: '4. Facial palsy',
    options: [
      { score: 0, label: 'Normal symmetric movement' },
      { score: 1, label: 'Minor paralysis (flattened nasolabial fold)' },
      { score: 2, label: 'Partial paralysis (lower face)' },
      { score: 3, label: 'Complete paralysis (upper and lower face)' },
    ],
  },
  motorArm('Left', '5L'),
  motorArm('Right', '5R'),
  motorLeg('Left', '6L'),
  motorLeg('Right', '6R'),
  {
    id: '7',
    label: '7. Limb ataxia',
    hint: 'Finger-nose-finger and heel-shin tests.',
    allowUntestable: true,
    options: [
      { score: 0, label: 'Absent' },
      { score: 1, label: 'Present in one limb' },
      { score: 2, label: 'Present in two limbs' },
    ],
  },
  {
    id: '8',
    label: '8. Sensory',
    options: [
      { score: 0, label: 'Normal; no sensory loss' },
      { score: 1, label: 'Mild-to-moderate sensory loss' },
      { score: 2, label: 'Severe to total sensory loss' },
    ],
  },
  {
    id: '9',
    label: '9. Best language',
    options: [
      { score: 0, label: 'No aphasia; normal' },
      { score: 1, label: 'Mild-to-moderate aphasia' },
      { score: 2, label: 'Severe aphasia' },
      { score: 3, label: 'Mute, global aphasia' },
    ],
  },
  {
    id: '10',
    label: '10. Dysarthria',
    allowUntestable: true,
    options: [
      { score: 0, label: 'Normal' },
      { score: 1, label: 'Mild-to-moderate dysarthria' },
      { score: 2, label: 'Severe dysarthria (unintelligible or mute)' },
    ],
  },
  {
    id: '11',
    label: '11. Extinction and inattention',
    options: [
      { score: 0, label: 'No abnormality' },
      { score: 1, label: 'Inattention or extinction in one modality' },
      { score: 2, label: 'Profound hemi-inattention or extinction in > 1 modality' },
    ],
  },
];

export function scoreNihss(answers: ScoreAnswers) {
  return sumScore(NIHSS_ITEMS, answers);
}

export function interpretNihss(total: number): { severity: Severity; label: string } {
  if (total === 0) return { severity: 'normal', label: 'No stroke symptoms (0)' };
  if (total <= 4) return { severity: 'caution', label: 'Minor stroke (1–4)' };
  if (total <= 15) return { severity: 'critical', label: 'Moderate stroke (5–15)' };
  if (total <= 20) return { severity: 'critical', label: 'Moderate to severe stroke (16–20)' };
  return { severity: 'critical', label: 'Severe stroke (21–42)' };
}

// ---------------------------------------------------------------------------------------------
// Last known well
// ---------------------------------------------------------------------------------------------

export interface StrokeWindows {
  elapsedMinutes: number;
  withinThrombolyticWindow: boolean;
  withinThrombectomyWindow: boolean;
}

/**
 * Time since last known well. Thrombolytics are generally considered up to 4.5 h and
 * mechanical thrombectomy up to 24 h in selected patients. Destination decisions follow
 * regional stroke plans.
 */
export function strokeWindows(lastKnownWell: Date, now: Date = new Date()): StrokeWindows {
  const elapsedMinutes = Math.max(0, (now.getTime() - lastKnownWell.getTime()) / 60000);
  return {
    elapsedMinutes,
    withinThrombolyticWindow: elapsedMinutes <= 270,
    withinThrombectomyWindow: elapsedMinutes <= 1440,
  };
}
